// Match Chronic's pure computation — extracted out of MatchChronic.js (2026-07-28) so the new
// Full Physical Report can reuse the exact same match-target/scenario logic instead of hand-
// copying it a third time. MatchChronic.js imports these back in; behavior is unchanged.
// (MatchChronicPrint.js keeps its own independent copy — out of scope, untouched.)

// ── Match-day targets (MD lower bounds) ───────────────────────────────────────
export const MATCH_TARGETS = {
  CB:  { TD: 10675, HID:  592, SD: 136, HMLD: 1776, SA: 128 },
  FB:  { TD: 11163, HID: 1032, SD: 285, HMLD: 2542, SA: 185 },
  CDM: { TD: 12259, HID:  828, SD: 140, HMLD: 2253, SA: 165 },
  CAM: { TD: 11793, HID:  797, SD: 133, HMLD: 2222, SA: 167 },
  ST:  { TD: 10733, HID:  936, SD: 223, HMLD: 2233, SA: 157 },
  W:   { TD: 11035, HID: 1025, SD: 239, HMLD: 2535, SA: 187 },
};
export const BUILT_IN_SCENARIOS = ['1A','1B','1C','1D','1E','2A','2B','2C','3A','3B','3C','3D','4A','4B','4C','5A','6A','6B'];

// ── Helpers ────────────────────────────────────────────────────────────────────
export function findMatchScenario(scenarios, playerId, matchDate) {
  if (!matchDate) return null;
  const active = sc => sc.startDate <= matchDate;
  const pl = scenarios.filter(sc => String(sc.playerId) === String(playerId) && active(sc));
  if (pl.length) return pl.sort((a, b) => b.startDate.localeCompare(a.startDate))[0];
  const tm = scenarios.filter(sc => sc.playerId === 'team' && active(sc));
  return tm.length ? tm.sort((a, b) => b.startDate.localeCompare(a.startDate))[0] : null;
}

// A player's position as of a specific match date (2026-07-29) — mirrors findMatchScenario's
// "most recently started entry active on matchDate" pattern exactly, over the date-scoped
// player_positions table instead of scenarios. A player with no position_positions rows (the
// common case — this is opt-in per player) falls back to fallbackPosition (players.position),
// so this is purely additive: nothing changes for a player until someone records a position
// change for them.
export function findMatchPosition(positions, playerId, matchDate, fallbackPosition) {
  if (!matchDate) return fallbackPosition ?? null;
  const active = (positions || []).filter(p => String(p.playerId) === String(playerId) && p.startDate <= matchDate);
  if (!active.length) return fallbackPosition ?? null;
  return active.sort((a, b) => b.startDate.localeCompare(a.startDate))[0].position;
}

export const parseTimeToMinutes = t => {
  if (!t) return 0;
  const p = String(t).trim().split(':').map(Number);
  if (p.length === 3) return p[0] * 60 + p[1] + p[2] / 60;
  if (p.length === 2) return p[0] + p[1] / 60;
  return 0;
};

export const f0   = n => (n == null || isNaN(n) ? '—' : Math.round(n).toLocaleString());
export const f1   = n => (n == null || isNaN(n) ? '—' : n.toFixed(1));
export const fPct = n => (n == null || isNaN(n) ? '—' : Math.round(n) + '%');
export const fmtDate = d => { if (!d) return '—'; const [y,m,day]=d.split('-'); return `${day}/${m}/${y}`; };

// ── Per-minute average row: sum(param) / sum(mins played) across rows ─────────
export function computePerMinRow(rows) {
  const totalMins = rows.reduce((s, r) => s + (r.mins || 0), 0);
  if (!totalMins) return null;
  const perMin = key => rows.reduce((s, r) => s + (r[key] || 0), 0) / totalMins;
  return { td: perMin('td'), hid: perMin('hid'), sd: perMin('sd'), hmld: perMin('hmld'), sa: perMin('sa') };
}

// ── Pure function: compute per-match rows for any player ──────────────────────
// `positions` (optional, defaults to []): date-scoped player_positions rows — when a player has
// history entries covering a given match date, that match's target/green-flag comparison uses
// the position played THAT match rather than the player's current players.position. Each
// returned row also carries `matchPosition` so callers needing "who played this position this
// specific match" (e.g. the Full Physical Report's Match Performance section) don't have to
// re-derive it.
export function computeMatchRows(playerDrills, player, opponents, scenarios, customTemplates, positions = []) {
  const bySession = {};
  (playerDrills || []).forEach(d => {
    if (!bySession[d.session_id]) {
      bySession[d.session_id] = {
        sessionId: d.session_id,
        date: (d.session_date || '').slice(0, 10),
        title: d.session_title || '',
        opponentId: d.opponent_id,
        drills: [],
      };
    }
    bySession[d.session_id].drills.push(d);
  });
  return Object.values(bySession).map(s => {
    const opp = opponents.find(o => o.id === s.opponentId);
    const sc  = findMatchScenario(scenarios, player?.id, s.date);
    const pos = findMatchPosition(positions, player?.id, s.date, player?.position);
    let tgt   = null;
    if (sc && pos) {
      const tmpl = customTemplates.find(t => t.name === sc.scenario);
      if (tmpl) {
        const mdRow = tmpl.targets?.['MD']?.[pos];
        if (mdRow) tgt = { TD: mdRow.TD?.lower, HID: mdRow.HID?.lower, SD: mdRow.SD?.lower, HMLD: mdRow.HMLD?.lower, SA: mdRow.SA?.lower };
      } else if (BUILT_IN_SCENARIOS.includes(sc.scenario)) {
        tgt = MATCH_TARGETS[pos] || null;
      }
    }
    const sum    = key => s.drills.reduce((a, d) => a + (parseFloat(d[key]) || 0), 0);
    const td     = sum('td');  const hid  = sum('hid'); const sd   = sum('sd');
    const hmld   = sum('hmld'); const sa  = sum('sa');  const expl = sum('expl');
    const maxSpd = Math.max(0, ...s.drills.map(d => parseFloat(d.max_speed) || 0));
    const mins   = s.drills.reduce((a, d) => a + parseTimeToMinutes(d.total_time), 0);
    const pct    = (v, t) => t && mins > 0 ? (v / mins) / (t / 90) * 100 : null;
    const atLeast = (v, t) => !!(t && mins > 0 && v >= t * (mins / 90));
    return {
      sessionId: s.sessionId, date: s.date, title: s.title,
      opponent: opp?.name || s.title || '—', oppLogo: opp?.logo_url,
      td, hid, sd, hmld, sa, expl, maxSpd, mins,
      matchPosition: pos,
      tdPct: pct(td,tgt?.TD), hidPct: pct(hid,tgt?.HID), sdPct: pct(sd,tgt?.SD),
      hmldPct: pct(hmld,tgt?.HMLD), saPct: pct(sa,tgt?.SA),
      tdGreen: atLeast(td,tgt?.TD), hidGreen: atLeast(hid,tgt?.HID), sdGreen: atLeast(sd,tgt?.SD),
      hmldGreen: atLeast(hmld,tgt?.HMLD), saGreen: atLeast(sa,tgt?.SA),
    };
  }).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

// ── Team-level match rows — same shape as computeMatchRows' output, one row per match,
// averaged across every player who has drill data for that match instead of one player's own
// numbers. Reuses computeMatchRows itself per player (so each player's %-of-target is still
// judged against THEIR OWN position's target, exactly like the individual view) rather than
// reimplementing the per-match math a second time, then averages those rows together by
// session. tdGreen/etc. here mean "the team averaged at/above target that match", not "every
// player did" — same >=100%-of-target threshold, just applied to the averaged %.
export function computeTeamMatchRows(allDrills, players, opponents, scenarios, customTemplates, positions = []) {
  const byPlayerName = {};
  (allDrills || []).forEach(d => {
    const key = (d.player_name || '').toLowerCase();
    if (!key) return;
    if (!byPlayerName[key]) byPlayerName[key] = [];
    byPlayerName[key].push(d);
  });

  const allPlayerRows = [];
  Object.entries(byPlayerName).forEach(([nameLower, drills]) => {
    const player = players.find(p => (p.name || '').toLowerCase() === nameLower);
    allPlayerRows.push(...computeMatchRows(drills, player, opponents, scenarios, customTemplates, positions));
  });

  const bySession = {};
  allPlayerRows.forEach(r => {
    if (!bySession[r.sessionId]) bySession[r.sessionId] = [];
    bySession[r.sessionId].push(r);
  });

  return Object.values(bySession).map(rows => {
    const n = rows.length;
    const avg = key => rows.reduce((s, r) => s + (r[key] || 0), 0) / n;
    const avgPct = key => {
      const vals = rows.map(r => r[key]).filter(v => v != null);
      return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    };
    const first = rows[0];
    const tdPct = avgPct('tdPct'), hidPct = avgPct('hidPct'), sdPct = avgPct('sdPct'),
      hmldPct = avgPct('hmldPct'), saPct = avgPct('saPct');
    return {
      sessionId: first.sessionId, date: first.date, title: first.title,
      opponent: first.opponent, oppLogo: first.oppLogo,
      td: avg('td'), hid: avg('hid'), sd: avg('sd'), hmld: avg('hmld'), sa: avg('sa'), expl: avg('expl'),
      maxSpd: Math.max(0, ...rows.map(r => r.maxSpd || 0)),
      mins: avg('mins'),
      tdPct, hidPct, sdPct, hmldPct, saPct,
      tdGreen: tdPct != null && tdPct >= 100, hidGreen: hidPct != null && hidPct >= 100,
      sdGreen: sdPct != null && sdPct >= 100, hmldGreen: hmldPct != null && hmldPct >= 100,
      saGreen: saPct != null && saPct >= 100,
      playerCount: n,
    };
  }).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}
